
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DashboardStats from "@/components/dashboard/DashboardStats";
import HeroSection from "@/components/home/HeroSection";
import HowItWorksSection from "@/components/home/HowItWorksSection";
import LatestReagentsSection from "@/components/home/LatestReagentsSection";
import TestimonialsSection from "@/components/home/TestimonialsSection";
import CTASection from "@/components/home/CTASection";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { fetchReagents } from "@/services/reagentService";
import { useAuth } from "@/contexts/auth";

const DashboardPage = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dataLoadedRef = useRef(false);
  const toastShownRef = useRef(false);
  
  // Redirect non-authenticated users to the home page
  useEffect(() => {
    if (!user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  // Load reagent data once - optimized to reduce redundant data fetching
  useEffect(() => {
    // Skip prefetching if we've already done it or if data is already cached
    if (dataLoadedRef.current) return;
    
    // Check if we already have data in the cache
    const existingData = queryClient.getQueryData(['reagents']) || queryClient.getQueryData(['latestReagents']);
    
    // Only show loading toast if we don't have cached data and haven't shown one yet
    const showToast = !existingData && !toastShownRef.current;
    if (showToast) {
      toast.info("Loading latest reagents...");
      toastShownRef.current = true;
    }
    
    // Use a single timeout to avoid multiple fetches
    let timeoutId: number | undefined;
    
    const loadReagents = async () => {
      try {
        // Check if we're already loading the query
        const query = queryClient.getQueryState(['latestReagents']);
        if (query && (query.status === 'pending' || query.fetchStatus === 'fetching')) {
          return;
        }
        
        // Only prefetch if user is authenticated
        if (!user) return;
        
        // Prefetch reagents directly to ensure data is loaded
        const data = await fetchReagents();
        
        if (data && data.length > 0) {
          // Set the prefetched data in the query cache
          queryClient.setQueryData(['reagents'], data);
          queryClient.setQueryData(['latestReagents'], data);
          
          if (showToast) {
            toast.success(`Loaded reagents successfully`);
          }
        } else if (showToast) {
          toast.info("No reagents found in the database");
        }
        
        // Mark as loaded to prevent duplicate fetches
        dataLoadedRef.current = true;
      } catch (error) {
        if (showToast) {
          toast.error("Error loading reagents: " + (error instanceof Error ? error.message : "Unknown error"));
        }
      }
    };
    
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    // Start loading reagents with a small delay to allow for component mounting
    timeoutId = window.setTimeout(() => {
      loadReagents();
    }, 500);
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [queryClient, user]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main>
        <DashboardStats />
        <HeroSection />
        <HowItWorksSection />
        <LatestReagentsSection />
        <TestimonialsSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default React.memo(DashboardPage);
